import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Alert, AlertDescription } from '../components/ui/alert';
import { 
  Package, Plus, Warehouse, AlertTriangle, CheckCircle, Clock, Filter,
  Users, Baby, User, ShoppingBag, Info, Edit, Trash2
} from 'lucide-react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend
} from 'recharts';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SEXO_LABELS = { M: "Masculino", F: "Feminino", Unissexo: "Unissexo" };
const FAIXA_ETARIA_LABELS = {
  bebe: "Bebé (0-2 anos)",
  crianca: "Criança (3-10 anos)",
  jovem: "Jovem (11-18 anos)",
  adulto: "Adulto (18-64 anos)",
  idoso: "Idoso (65+ anos)"
};

const CONDICAO_COLORS = {
  "Novo": "bg-green-500",
  "Bom": "bg-blue-500",
  "Usado": "bg-amber-500"
};

const CATEGORIA_COLORS = ['#0066CC', '#00AA66', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

// API helper
const apiCall = async (endpoint, options = {}) => {
  const token = localStorage.getItem('token');
  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    ...options.headers
  };
  
  const response = await fetch(`${API_URL}/api${endpoint}`, { ...options, headers });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Erro' }));
    throw error;
  }
  return response.json();
};

export default function RecursosPage() {
  const [dashboard, setDashboard] = useState(null);
  const [stock, setStock] = useState([]);
  const [tiposRecursos, setTiposRecursos] = useState({});
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [filtroTipo, setFiltroTipo] = useState('');
  const [filtroSexo, setFiltroSexo] = useState('');
  const [filtroFaixa, setFiltroFaixa] = useState('');
  
  // Modal
  const [modalOpen, setModalOpen] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [tamanhos, setTamanhos] = useState([]);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  
  // Form
  const [form, setForm] = useState({
    tipo_recurso: '',
    quantidade_disponivel: 1,
    armazem: 'Lisboa',
    condicao: 'Novo',
    doador: '',
    sexo: '',
    faixa_etaria: '',
    tamanho: '',
    tipo_peca: '',
    estacao: '',
    observacoes: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [dashData, stockData, tiposData] = await Promise.all([
        apiCall('/recursos/dashboard'),
        apiCall('/recursos/stock'),
        apiCall('/tipos-recursos')
      ]);
      setDashboard(dashData);
      setStock(stockData || []);
      setTiposRecursos(tiposData || {});
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  // Load sizes when age group changes
  const loadTamanhos = async (tipoRecurso, faixaEtaria) => {
    if (!tipoRecurso || !faixaEtaria) {
      setTamanhos([]);
      return;
    }
    try {
      const res = await apiCall(`/tipos-recursos/${tipoRecurso}/tamanhos?faixa_etaria=${faixaEtaria}`);
      setTamanhos(res?.tamanhos || []);
    } catch {
      setTamanhos([]);
    }
  };

  const handleTipoRecursoChange = (value) => {
    setForm(prev => ({ ...prev, tipo_recurso: value, sexo: '', faixa_etaria: '', tamanho: '' }));
    setTamanhos([]);
  };

  const handleFaixaChange = (value) => {
    setForm(prev => ({ ...prev, faixa_etaria: value, tamanho: '' }));
    loadTamanhos(form.tipo_recurso, value);
  };

  const handleCreateStock = async () => {
    if (!form.tipo_recurso || form.quantidade_disponivel <= 0) {
      toast.error('Preencha o tipo de recurso e quantidade');
      return;
    }
    
    setFormLoading(true);
    try {
      if (editingItem) {
        await apiCall(`/recursos/stock/${editingItem.id}`, {
          method: 'PUT',
          body: JSON.stringify(form)
        });
        toast.success('Stock atualizado com sucesso!');
      } else {
        await apiCall('/recursos/stock', {
          method: 'POST',
          body: JSON.stringify(form)
        });
        toast.success('Stock adicionado com sucesso!');
      }
      
      setModalOpen(false);
      setEditingItem(null);
      setForm({
        tipo_recurso: '', quantidade_disponivel: 1, armazem: 'Lisboa', condicao: 'Novo',
        doador: '', sexo: '', faixa_etaria: '', tamanho: '', tipo_peca: '', estacao: '', observacoes: ''
      });
      loadData();
    } catch (error) {
      toast.error(error.detail || 'Erro ao guardar stock');
    } finally {
      setFormLoading(false);
    }
  };

  const handleEditItem = (item) => {
    setEditingItem(item);
    setForm({
      tipo_recurso: item.tipo_recurso || '',
      quantidade_disponivel: item.quantidade_disponivel || 1,
      armazem: item.armazem || 'Lisboa',
      condicao: item.condicao || 'Novo',
      doador: item.doador || '',
      sexo: item.sexo || '',
      faixa_etaria: item.faixa_etaria || '',
      tamanho: item.tamanho || '',
      tipo_peca: item.tipo_peca || '',
      estacao: item.estacao || '',
      observacoes: item.observacoes || ''
    });
    if (item.tipo_recurso && item.faixa_etaria) {
      loadTamanhos(item.tipo_recurso, item.faixa_etaria);
    }
    setModalOpen(true);
  };

  const handleDeleteItem = async () => {
    if (!itemToDelete) return;
    
    try {
      await apiCall(`/recursos/stock/${itemToDelete.id}`, { method: 'DELETE' });
      toast.success('Stock eliminado com sucesso!');
      setDeleteConfirmOpen(false);
      setItemToDelete(null);
      loadData();
    } catch (error) {
      toast.error(error.detail || 'Erro ao eliminar stock');
    }
  };

  const openDeleteConfirm = (item) => {
    setItemToDelete(item);
    setDeleteConfirmOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingItem(null);
    setForm({
      tipo_recurso: '', quantidade_disponivel: 1, armazem: 'Lisboa', condicao: 'Novo',
      doador: '', sexo: '', faixa_etaria: '', tamanho: '', tipo_peca: '', estacao: '', observacoes: ''
    });
  };

  // Get current tipo config
  const currentTipoConfig = tiposRecursos[form.tipo_recurso];

  // Filtered stock
  const filteredStock = stock.filter(s => {
    if (filtroTipo && filtroTipo !== 'all' && s.tipo_recurso !== filtroTipo) return false;
    if (filtroSexo && filtroSexo !== 'all' && s.sexo !== filtroSexo) return false;
    if (filtroFaixa && filtroFaixa !== 'all' && s.faixa_etaria !== filtroFaixa) return false;
    return true;
  });

  // Chart data
  const stockPorTipo = dashboard?.stock_por_tipo 
    ? Object.entries(dashboard.stock_por_tipo).map(([name, value]) => ({ name, value }))
    : [];
  
  const stockPorSexo = dashboard?.stock_por_sexo
    ? Object.entries(dashboard.stock_por_sexo)
        .filter(([k]) => k !== 'Não especificado' || dashboard.stock_por_sexo[k] > 0)
        .map(([name, value]) => ({ name: SEXO_LABELS[name] || name, value }))
    : [];

  const stockPorFaixa = dashboard?.stock_por_faixa_etaria
    ? Object.entries(dashboard.stock_por_faixa_etaria)
        .filter(([k]) => k !== 'Não especificado' || dashboard.stock_por_faixa_etaria[k] > 0)
        .map(([name, value]) => ({ name: FAIXA_ETARIA_LABELS[name] || name, value }))
    : [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="w-8 h-8 border-2 border-slate-200 border-t-blue-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="recursos-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Gestão de Stock</h1>
          <p className="text-slate-500 mt-1">Controlo de stock com atributos demográficos</p>
        </div>
        <Button onClick={() => setModalOpen(true)} data-testid="add-stock-btn">
          <Plus className="w-4 h-4 mr-2" />
          Adicionar Stock
        </Button>
      </div>

      {/* Info Alert */}
      <Alert className="bg-blue-50 border-blue-200">
        <Info className="h-4 w-4 text-blue-600" />
        <AlertDescription className="text-blue-700">
          O stock agora inclui atributos demográficos (sexo, faixa etária, tamanho) para permitir 
          matching automático com as necessidades das instituições.
        </AlertDescription>
      </Alert>

      {/* Dashboard Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Disponível</p>
                <p className="text-2xl font-bold text-slate-900 mt-1">
                  {dashboard?.total_disponivel?.toLocaleString('pt-PT') || 0}
                </p>
                <p className="text-xs text-slate-400">{dashboard?.total_itens_stock || 0} itens</p>
              </div>
              <Warehouse className="w-8 h-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-slate-500">Reservado</p>
                <p className="text-2xl font-bold text-amber-600 mt-1">
                  {dashboard?.total_reservado?.toLocaleString('pt-PT') || 0}
                </p>
              </div>
              <Clock className="w-8 h-8 text-amber-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-slate-500">Alocado</p>
                <p className="text-2xl font-bold text-green-600 mt-1">
                  {dashboard?.total_alocado?.toLocaleString('pt-PT') || 0}
                </p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-slate-500">Necessidades Pendentes</p>
                <p className="text-2xl font-bold text-red-600 mt-1">
                  {dashboard?.necessidades_pendentes || 0}
                </p>
              </div>
              <AlertTriangle className="w-8 h-8 text-red-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Por Tipo de Recurso</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[200px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stockPorTipo.slice(0, 6)} layout="vertical">
                  <XAxis type="number" />
                  <YAxis dataKey="name" type="category" width={80} tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Bar dataKey="value" fill="#0066CC" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Por Sexo</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[200px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={stockPorSexo}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={70}
                    dataKey="value"
                    label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}
                  >
                    {stockPorSexo.map((_, index) => (
                      <Cell key={index} fill={CATEGORIA_COLORS[index % CATEGORIA_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Por Faixa Etária</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[200px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stockPorFaixa}>
                  <XAxis dataKey="name" tick={{ fontSize: 8 }} interval={0} angle={-45} textAnchor="end" height={60} />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="value" fill="#00AA66" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Filter className="w-4 h-4" />
            Filtros
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Select value={filtroTipo} onValueChange={setFiltroTipo}>
              <SelectTrigger>
                <SelectValue placeholder="Tipo de Recurso" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {Object.entries(tiposRecursos).map(([key, config]) => (
                  <SelectItem key={key} value={key}>{config.nome}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filtroSexo} onValueChange={setFiltroSexo}>
              <SelectTrigger>
                <SelectValue placeholder="Sexo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="M">Masculino</SelectItem>
                <SelectItem value="F">Feminino</SelectItem>
                <SelectItem value="Unissexo">Unissexo</SelectItem>
              </SelectContent>
            </Select>

            <Select value={filtroFaixa} onValueChange={setFiltroFaixa}>
              <SelectTrigger>
                <SelectValue placeholder="Faixa Etária" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                {Object.entries(FAIXA_ETARIA_LABELS).map(([key, label]) => (
                  <SelectItem key={key} value={key}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Stock Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Inventário ({filteredStock.length})</span>
            {(filtroTipo && filtroTipo !== 'all') || (filtroSexo && filtroSexo !== 'all') || (filtroFaixa && filtroFaixa !== 'all') ? (
              <Button variant="ghost" size="sm" onClick={() => { setFiltroTipo(''); setFiltroSexo(''); setFiltroFaixa(''); }}>
                Limpar Filtros
              </Button>
            ) : null}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Tipo</TableHead>
                <TableHead>Atributos</TableHead>
                <TableHead>Tamanho</TableHead>
                <TableHead className="text-center">Qtd</TableHead>
                <TableHead>Armazém</TableHead>
                <TableHead>Condição</TableHead>
                <TableHead>Doador</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredStock.slice(0, 50).map((item) => (
                <TableRow key={item.id}>
                  <TableCell className="font-medium">
                    {item.tipo_recurso_nome || item.tipo_recurso}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {item.sexo && (
                        <Badge variant="outline" className="text-xs">
                          {SEXO_LABELS[item.sexo] || item.sexo}
                        </Badge>
                      )}
                      {item.faixa_etaria && (
                        <Badge variant="outline" className="text-xs">
                          {FAIXA_ETARIA_LABELS[item.faixa_etaria]?.split(' ')[0] || item.faixa_etaria}
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>{item.tamanho || '-'}</TableCell>
                  <TableCell className="text-center font-semibold">
                    {item.quantidade_disponivel}
                    {item.quantidade_alocada > 0 && (
                      <span className="text-xs text-slate-400 block">
                        ({item.quantidade_alocada} alocado)
                      </span>
                    )}
                  </TableCell>
                  <TableCell>{item.armazem}</TableCell>
                  <TableCell>
                    <Badge className={CONDICAO_COLORS[item.condicao] || 'bg-slate-500'}>
                      {item.condicao}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-slate-500">{item.doador || '-'}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="sm" onClick={() => handleEditItem(item)} title="Editar">
                        <Edit className="w-4 h-4 text-blue-600" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => openDeleteConfirm(item)} title="Eliminar">
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {filteredStock.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-slate-500 py-8">
                    Nenhum item em stock
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add/Edit Stock Modal */}
      <Dialog open={modalOpen} onOpenChange={handleCloseModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Editar Stock' : 'Adicionar Stock'}</DialogTitle>
            <DialogDescription>
              {editingItem ? 'Atualize os dados do stock' : 'Registe novo stock com atributos demográficos'}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            {/* Tipo de Recurso */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tipo de Recurso *</Label>
                <Select value={form.tipo_recurso} onValueChange={handleTipoRecursoChange}>
                  <SelectTrigger data-testid="select-tipo">
                    <SelectValue placeholder="Selecione..." />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(tiposRecursos).map(([key, config]) => (
                      <SelectItem key={key} value={key}>
                        <div className="flex items-center gap-2">
                          <Package className="h-4 w-4" />
                          {config.nome}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Quantidade *</Label>
                <Input
                  type="number"
                  min="1"
                  value={form.quantidade_disponivel}
                  onChange={(e) => setForm(prev => ({ ...prev, quantidade_disponivel: parseInt(e.target.value) || 1 }))}
                />
              </div>
            </div>

            {/* Atributos demográficos - só mostrar para tipos que validam utentes */}
            {currentTipoConfig?.requer_validacao_utentes && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  {currentTipoConfig.atributos?.some(a => a.nome === 'sexo') && (
                    <div className="space-y-2">
                      <Label>Sexo</Label>
                      <Select value={form.sexo} onValueChange={(v) => setForm(prev => ({ ...prev, sexo: v }))}>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione..." />
                        </SelectTrigger>
                        <SelectContent>
                          {currentTipoConfig.atributos.find(a => a.nome === 'sexo')?.opcoes.map(opt => (
                            <SelectItem key={opt} value={opt}>{SEXO_LABELS[opt] || opt}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {currentTipoConfig.atributos?.some(a => a.nome === 'faixa_etaria') && (
                    <div className="space-y-2">
                      <Label>Faixa Etária</Label>
                      <Select value={form.faixa_etaria} onValueChange={handleFaixaChange}>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione..." />
                        </SelectTrigger>
                        <SelectContent>
                          {currentTipoConfig.atributos.find(a => a.nome === 'faixa_etaria')?.opcoes.map(opt => (
                            <SelectItem key={opt} value={opt}>{FAIXA_ETARIA_LABELS[opt] || opt}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>

                {tamanhos.length > 0 && (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Tamanho</Label>
                      <Select value={form.tamanho} onValueChange={(v) => setForm(prev => ({ ...prev, tamanho: v }))}>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione..." />
                        </SelectTrigger>
                        <SelectContent>
                          {tamanhos.map(t => (
                            <SelectItem key={t} value={t}>{t}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {currentTipoConfig.atributos?.some(a => a.nome === 'tipo') && (
                      <div className="space-y-2">
                        <Label>Tipo de Peça</Label>
                        <Select value={form.tipo_peca} onValueChange={(v) => setForm(prev => ({ ...prev, tipo_peca: v }))}>
                          <SelectTrigger>
                            <SelectValue placeholder="Opcional..." />
                          </SelectTrigger>
                          <SelectContent>
                            {currentTipoConfig.atributos.find(a => a.nome === 'tipo')?.opcoes.map(opt => (
                              <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            {/* Armazém e Condição */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Armazém</Label>
                <Select value={form.armazem} onValueChange={(v) => setForm(prev => ({ ...prev, armazem: v }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Lisboa">Lisboa</SelectItem>
                    <SelectItem value="Porto">Porto</SelectItem>
                    <SelectItem value="Coimbra">Coimbra</SelectItem>
                    <SelectItem value="Faro">Faro</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Condição</Label>
                <Select value={form.condicao} onValueChange={(v) => setForm(prev => ({ ...prev, condicao: v }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Novo">Novo</SelectItem>
                    <SelectItem value="Bom">Bom</SelectItem>
                    <SelectItem value="Usado">Usado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Doador</Label>
              <Input
                placeholder="Nome do doador (opcional)"
                value={form.doador}
                onChange={(e) => setForm(prev => ({ ...prev, doador: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label>Observações</Label>
              <Textarea
                placeholder="Notas adicionais..."
                value={form.observacoes}
                onChange={(e) => setForm(prev => ({ ...prev, observacoes: e.target.value }))}
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleCloseModal}>Cancelar</Button>
            <Button onClick={handleCreateStock} disabled={formLoading || !form.tipo_recurso}>
              {formLoading ? 'A guardar...' : (editingItem ? 'Atualizar Stock' : 'Adicionar Stock')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">Confirmar Eliminação</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja eliminar este item do stock?
              {itemToDelete && (
                <div className="mt-2 p-3 bg-slate-50 rounded-lg">
                  <p className="font-medium">{itemToDelete.tipo_recurso_nome || itemToDelete.tipo_recurso}</p>
                  <p className="text-sm text-slate-500">Quantidade: {itemToDelete.quantidade_disponivel}</p>
                </div>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)}>Cancelar</Button>
            <Button variant="destructive" onClick={handleDeleteItem}>Eliminar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
